import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { dashboardApi } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { ShieldCheck, X, ArrowRight, Sparkles } from "lucide-react";

// Severity → tailwind tokens. Each severity gets its own coloured left
// border (4px), accent text colour and matching badge tint.
const SEVERITY_TOKENS = {
  critical: {
    border: "border-l-[4px] border-l-[#EF4444]",
    title: "text-[#EF4444]",
    badge: "bg-red-500/15 text-red-300",
    label: "Critical",
  },
  high: {
    border: "border-l-[4px] border-l-[#F59E0B]",
    title: "text-[#F59E0B]",
    badge: "bg-amber-500/15 text-amber-300",
    label: "High",
  },
  medium: {
    border: "border-l-[4px] border-l-[#3B82F6]",
    title: "text-[#3B82F6]",
    badge: "bg-blue-500/15 text-blue-300",
    label: "Medium",
  },
  info: {
    border: "border-l-[4px] border-l-[#3B82F6]",
    title: "text-[#3B82F6]",
    badge: "bg-blue-500/15 text-blue-300",
    label: "Info",
  },
  positive: {
    border: "border-l-[4px] border-l-[#10B981]",
    title: "text-[#10B981]",
    badge: "bg-emerald-500/15 text-emerald-300",
    label: "Positive",
  },
};

const MAX_VISIBLE = 5;
const SESSION_DISMISS_KEY = "sv_sentinel_dismissed";

function loadDismissed() {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(SESSION_DISMISS_KEY) || "[]"));
  } catch {
    return new Set();
  }
}

function saveDismissed(set) {
  sessionStorage.setItem(SESSION_DISMISS_KEY, JSON.stringify([...set]));
}

function SkeletonCard() {
  // Skeleton card — keeps the panel intentional-looking while
  // the deterministic compute + LLM narrative runs.
  return (
    <div
      className="rounded-lg border border-slate-800/40 bg-slate-900/40 p-4"
      data-testid="sentinel-skeleton-card"
    >
      <div className="h-3 w-32 bg-slate-700/50 rounded mb-3 animate-pulse" />
      <div className="h-2.5 w-full bg-slate-700/30 rounded mb-2 animate-pulse [animation-delay:120ms]" />
      <div className="h-2.5 w-5/6 bg-slate-700/30 rounded animate-pulse [animation-delay:240ms]" />
    </div>
  );
}

export default function SentinelInsightsPanel() {
  const navigate = useNavigate();
  const [insights, setInsights] = useState(null); // null = loading
  const [dismissed, setDismissed] = useState(loadDismissed);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await dashboardApi.sentinelInsights();
        if (!cancelled) setInsights(res?.data?.insights || []);
      } catch (e) {
        if (!cancelled) {
          setInsights([]);
          setError(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleDismiss = (id) => {
    const next = new Set(dismissed);
    next.add(id);
    setDismissed(next);
    saveDismissed(next);
  };

  // Loading shape — 3 skeleton cards.
  if (insights === null) {
    return (
      <section data-testid="sentinel-insights-panel">
        <Header />
        <Card className="bg-slate-900/40 border-slate-800/50">
          <CardContent className="p-4 md:p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </div>
          </CardContent>
        </Card>
      </section>
    );
  }

  const visible = insights
    .filter((i) => !dismissed.has(i.id))
    .slice(0, MAX_VISIBLE);

  // Empty state — no committed data yet OR everything dismissed.
  if (visible.length === 0) {
    return (
      <section data-testid="sentinel-insights-panel">
        <Header />
        <Card className="bg-slate-900/40 border-slate-800/50">
          <CardContent className="p-6">
            <div
              className="text-sm text-slate-400 text-center py-6"
              data-testid="sentinel-empty-state"
            >
              {error
                ? "Sentinel AI is briefly unavailable — refresh in a moment."
                : insights.length === 0
                ? "Upload and commit financial data to unlock Sentinel AI financial behaviour insights."
                : "All insights dismissed for this session."}
            </div>
            <Footer />
          </CardContent>
        </Card>
      </section>
    );
  }

  return (
    <section data-testid="sentinel-insights-panel">
      <Header />
      <Card className="bg-slate-900/40 border-slate-800/50">
        <CardContent className="p-4 md:p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {visible.map((ins) => {
              const tone = SEVERITY_TOKENS[ins.severity] || SEVERITY_TOKENS.info;
              return (
                <div
                  key={ins.id}
                  className={`relative rounded-lg bg-slate-900/60 border border-slate-800/40 ${tone.border} p-4 pr-8 transition-colors hover:bg-slate-900/80`}
                  data-testid={`sentinel-card-${ins.id}`}
                >
                  <button
                    onClick={() => handleDismiss(ins.id)}
                    className="absolute top-2.5 right-2.5 text-slate-500 hover:text-slate-300 transition-colors"
                    aria-label="Dismiss insight"
                    data-testid={`sentinel-dismiss-${ins.id}`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                  <div className="flex items-center gap-2 mb-2">
                    <span
                      className={`text-[9px] px-1.5 py-0.5 rounded uppercase tracking-[0.12em] font-semibold ${tone.badge}`}
                    >
                      {tone.label}
                    </span>
                  </div>
                  <div
                    className={`text-sm font-semibold leading-tight mb-1.5 ${tone.title}`}
                    data-testid={`sentinel-card-title-${ins.id}`}
                  >
                    {ins.title}
                  </div>
                  <p
                    className="text-xs text-slate-300 leading-relaxed"
                    data-testid={`sentinel-card-message-${ins.id}`}
                  >
                    {ins.message || ins.fallback_copy}
                  </p>
                  {ins.action && (
                    <button
                      onClick={() => navigate(ins.action.route)}
                      className={`mt-2.5 inline-flex items-center gap-1 text-[11px] font-medium ${tone.title} hover:underline`}
                      data-testid={`sentinel-action-${ins.id}`}
                    >
                      {ins.action.label}
                      <ArrowRight className="w-3 h-3" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          <Footer />
        </CardContent>
      </Card>
    </section>
  );
}

function Header() {
  return (
    <div className="flex items-center justify-between mb-3">
      <h2
        className="font-barlow font-semibold text-xs text-slate-300 uppercase tracking-[0.2em] flex items-center gap-2"
        data-testid="sentinel-panel-title"
      >
        <ShieldCheck className="w-3.5 h-3.5 text-cyan-400" />
        Sentinel AI — Financial Behaviour Analysis
      </h2>
    </div>
  );
}

function Footer() {
  return (
    <div
      className="mt-4 pt-3 border-t border-slate-800/30 flex items-center justify-end gap-1 text-[10px] text-slate-500"
      data-testid="sentinel-powered-by"
    >
      <Sparkles className="w-2.5 h-2.5" />
      Powered by Sentinel AI
    </div>
  );
}
